/**
 * ⟦C62 · lot atelier · friction 7⟧ **Une note sur 100, et un mot.**
 *
 * L'audit « regard neuf » du 21/10/2026 : « Script bouclé. 14 », « Coupez ! 0 », « SCORE D'EXÉCUTION 49 »
 * en rouge pour une vidéo « Standard ». Un joueur qui découvre le jeu ne sait pas si 14, 49 ou 72 sont
 * bons : le chiffre n'a pas d'échelle, et le rouge d'un 49 dit « raté » à qui a fait un score moyen.
 * Désormais : « 14 / 100 », et un mot : Raté, Correct, Bien, Parfait.
 *
 * ## Les seuils ne sont pas inventés, ils sont ceux du jeu
 *
 * | Mot | Note | Pourquoi là |
 * |---|---|---|
 * | **Raté** | moins de 30 | `EXECUTION_NULLE` : sous 30, la vidéo est nulle (§4.8, §6.1) |
 * | **Correct** | 30 à 59 | la vidéo passe, sans plus |
 * | **Bien** | 60 à 89 | `SEUIL_JALON_REUSSI` : 60, la barre d'un jalon |
 * | **Parfait** | 90 et plus | le bonus de viralité (déjà dit à l'écran d'une étape à 90) |
 *
 * Deux seuils viennent du moteur (importés, jamais recopiés : si le moteur bouge, les mots suivent) ; le
 * troisième, 90, est celui que l'Atelier affichait déjà (« Bonus de viralité ! »). Aucun barème, aucun
 * timing ne change : c'est une lecture de la note, pas un calcul.
 *
 * Pur : aucun DOM, aucun store. Les mots du joueur sont ici, une seule fois, pour les cinq mini-jeux, le
 * récap et l'écran de publication.
 */

import { EXECUTION_NULLE, SEUIL_JALON_REUSSI } from "@maxime/shared";

/** Le seuil de « Correct » : en dessous, « Raté » (la vidéo est nulle sous ce score). */
export const SEUIL_NOTE_CORRECT = EXECUTION_NULLE;
/** Le seuil de « Bien » : la barre d'un jalon. */
export const SEUIL_NOTE_BIEN = SEUIL_JALON_REUSSI;
/** Le seuil de « Parfait » : le bonus de viralité. */
export const SEUIL_NOTE_PARFAIT = 90;

export type MotNote = "Raté" | "Correct" | "Bien" | "Parfait";
/** La classe CSS de la pastille (`note-mot--rate`, …) : une couleur par mot, jamais le rouge d'un score moyen. */
export type TonDeNote = "rate" | "correct" | "bien" | "parfait";

export interface LectureDeNote {
  mot: MotNote;
  ton: TonDeNote;
}

/** Le mot d'une note. La note est arrondie comme à l'écran : celle qu'on lit est celle qu'on juge. */
export function noteEnMot(note: number): LectureDeNote {
  const n = Math.round(note);
  if (n < SEUIL_NOTE_CORRECT) return { mot: "Raté", ton: "rate" };
  if (n < SEUIL_NOTE_BIEN) return { mot: "Correct", ton: "correct" };
  if (n < SEUIL_NOTE_PARFAIT) return { mot: "Bien", ton: "bien" };
  return { mot: "Parfait", ton: "parfait" };
}

/** L'échelle entière, dite au joueur dans « Les dessous » : chaque mot, sa fourchette, ce qu'elle veut dire. */
export function echelleDesNotes(): readonly { mot: MotNote; fourchette: string; sens: string }[] {
  return [
    { mot: "Raté", fourchette: `moins de ${SEUIL_NOTE_CORRECT}`, sens: "la vidéo risque d'être nulle" },
    { mot: "Correct", fourchette: `${SEUIL_NOTE_CORRECT} à ${SEUIL_NOTE_BIEN - 1}`, sens: "ça passe, sans plus" },
    { mot: "Bien", fourchette: `${SEUIL_NOTE_BIEN} à ${SEUIL_NOTE_PARFAIT - 1}`, sens: "la barre d'un jalon" },
    { mot: "Parfait", fourchette: `${SEUIL_NOTE_PARFAIT} et plus`, sens: "bonus de viralité, si ta chaîne le retient" },
  ];
}
